import { saveTaxInformation as saveTaxInformationApi } from '../../util/api';
import { storableError } from '../../util/errors';
import { fetchCurrentUser } from '../../ducks/user.duck';
import { EU_VAT_AREA } from '../../util/vat';

// ================ Action types ================ //

export const SAVE_TAX_INFORMATION_REQUEST = 'app/TaxInformationPage/SAVE_TAX_INFORMATION_REQUEST';
export const SAVE_TAX_INFORMATION_SUCCESS = 'app/TaxInformationPage/SAVE_TAX_INFORMATION_SUCCESS';
export const SAVE_TAX_INFORMATION_ERROR = 'app/TaxInformationPage/SAVE_TAX_INFORMATION_ERROR';

export const SAVE_TAX_INFORMATION_CLEAR = 'app/TaxInformationPage/SAVE_TAX_INFORMATION_CLEAR';

// ================ Reducer ================ //

const initialState = {
  saveTaxInformationInProgress: false,
  taxInformationChanged: false,
  taxInformationError: null,
  taxInformationSuccess: false,
  // Sanitized echo from the save endpoint: { type, last4, issuedBy, submittedAt }
  lastSavedIdentifier: null,
  lastSavedVatValidationStatus: null,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case SAVE_TAX_INFORMATION_REQUEST:
      return {
        ...state,
        saveTaxInformationInProgress: true,
        taxInformationChanged: false,
        taxInformationError: null,
        taxInformationSuccess: false,
      };
    case SAVE_TAX_INFORMATION_SUCCESS: {
      // Only masked identifier types are useful for the form's masked
      // display; plaintext types (YTUNNUS) are prefilled from the record.
      const identifier = payload?.identifier;
      const isMaskedType = identifier && ['HETU', 'OTHER_TIN'].includes(identifier.type);
      return {
        ...state,
        saveTaxInformationInProgress: false,
        taxInformationChanged: true,
        taxInformationSuccess: true,
        lastSavedIdentifier: isMaskedType ? identifier : null,
        lastSavedVatValidationStatus: payload?.vatValidationStatus || null,
      };
    }
    case SAVE_TAX_INFORMATION_ERROR:
      return {
        ...state,
        saveTaxInformationInProgress: false,
        taxInformationChanged: false,
        taxInformationError: payload,
      };
    case SAVE_TAX_INFORMATION_CLEAR:
      return {
        ...state,
        saveTaxInformationInProgress: false,
        taxInformationChanged: false,
        taxInformationError: null,
        taxInformationSuccess: false,
        lastSavedVatValidationStatus: null,
      };
    default:
      return state;
  }
}

// ================ Action creators ================ //

export const saveTaxInformationRequest = () => ({ type: SAVE_TAX_INFORMATION_REQUEST });
export const saveTaxInformationSuccess = payload => ({
  type: SAVE_TAX_INFORMATION_SUCCESS,
  payload,
});
export const saveTaxInformationError = error => ({
  type: SAVE_TAX_INFORMATION_ERROR,
  payload: error,
  error: true,
});

export const saveContactDetailsClear = () => ({ type: SAVE_TAX_INFORMATION_CLEAR });

// Translate form values into the API payload shape expected by
// /api/save-tax-information. Plaintext HETU / TIN is on this object briefly
// and is encrypted by the server before being persisted.
const isEuCountry = country => EU_VAT_AREA.includes(country);

// Finnish Y-tunnus → ALV-tunnus (VAT). Strip the hyphen and prepend "FI".
// e.g. 3150366-3 → FI31503663
export const ytunnusToVat = ytunnus =>
  typeof ytunnus === 'string' && /^\d{7}-\d$/.test(ytunnus)
    ? `FI${ytunnus.replace('-', '')}`
    : null;

// The schema v2 VAT block from the form values. Rates are typed as percent
// strings ("20"), stored as decimals (0.2). Non-EU sellers are always
// REGISTERED (Notion Q4) with a self-declared home rate (Q11).
const vatBlock = (values, isNonEu) => {
  const status = isNonEu ? 'REGISTERED' : values.vatStatus || null;
  const clean = v => (typeof v === 'string' && v.trim() ? v.trim() : null);
  const rate = Number(String(values.homeVatRate || '0').replace(',', '.'));
  return {
    vatStatus: status,
    // REGISTERED always carries a number; EXEMPT only when the seller typed
    // one (optional field, for EU sales); INVOICING_SERVICE never.
    vatNumber:
      status === 'REGISTERED' || status === 'EXEMPT_SMALL_BUSINESS'
        ? clean(values.vatNumber)
        : null,
    exemptId: status === 'EXEMPT_SMALL_BUSINESS' ? clean(values.exemptId) : null,
    homeVat: isNonEu
      ? { rate: Number.isFinite(rate) ? rate / 100 : 0, label: clean(values.homeVatLabel) || 'VAT' }
      : null,
    invoicingService:
      status === 'INVOICING_SERVICE'
        ? {
            name: clean(values.invoicingServiceName),
            vatNumber: clean(values.invoicingServiceVatNumber),
          }
        : null,
  };
};

export const buildPayload = values => {
  const country = values.country;

  // Outside-EU sellers don't fall under DAC7 (no identifier, no birth info),
  // but VAT needs their country, address and home rate (spec §2).
  if (!isEuCountry(country)) {
    const isEntity = values.sellerType === 'BUSINESS';
    return {
      outsideEu: true,
      residency: { countryCodes: [country], outsideEu: true },
      name: {
        form: isEntity ? 'ENTITY' : 'INDIVIDUAL',
        firstName: isEntity ? null : values.firstName,
        lastName: isEntity ? null : values.lastName,
        legalName: isEntity ? values.legalName : null,
      },
      address: { street: values.street, postcode: values.postcode, city: values.city, country },
      ...vatBlock(values, true),
    };
  }

  const isFi = country === 'FI';

  // FI: explicit finCompanyForm radio drives the entity/individual split.
  // Non-FI: sellerType radio does the same.
  const isEntity = isFi ? values.finCompanyForm === 'ENTITY' : values.sellerType === 'BUSINESS';
  const nameForm = isEntity ? 'ENTITY' : 'INDIVIDUAL';
  const finHasYtunnus = isFi && !isEntity && !!values.finHasYtunnus;

  let identifier;
  if (isFi && isEntity) {
    identifier = { type: 'YTUNNUS', value: values.ytunnus, issuedBy: 'FI' };
  } else if (isFi && !isEntity) {
    // When the form shows the masked HETU the user hasn't re-typed it — ask
    // the server to keep the identifier already on record instead of
    // submitting an empty value (which would fail validation).
    identifier = values.hetuMasked
      ? { type: 'HETU', keepExisting: true, issuedBy: 'FI' }
      : { type: 'HETU', value: values.hetu, issuedBy: 'FI' };
  } else if (values.noTin) {
    identifier = { type: 'NONE' };
  } else {
    identifier = { type: 'OTHER_TIN', value: values.tin, issuedBy: values.tinCountry };
  }

  // FI individuals with a Y-tunnus store the Y-tunnus as a business
  // registration (the personal identifier is still HETU on `identifier`).
  // Non-FI businesses use the optional free-text business reg number.
  let businessRegistration = null;
  if (isFi && finHasYtunnus && values.ytunnus) {
    businessRegistration = { number: values.ytunnus, countryOfIssue: 'FI' };
  } else if (!isFi && isEntity && values.businessRegNumber) {
    businessRegistration = {
      number: values.businessRegNumber,
      countryOfIssue: values.businessRegCountry || country,
    };
  }

  // BirthInfo from FieldBirthdayInput is { year, month, day }
  const dob = values.dateOfBirth;
  const dobIso =
    dob && dob.year && dob.month && dob.day
      ? `${String(dob.year).padStart(4, '0')}-${String(dob.month).padStart(2, '0')}-${String(
          dob.day
        ).padStart(2, '0')}`
      : null;

  return {
    residency: { countryCodes: [country] },
    identifier,
    name: {
      form: nameForm,
      firstName: nameForm === 'INDIVIDUAL' ? values.firstName : null,
      lastName: nameForm === 'INDIVIDUAL' ? values.lastName : null,
      legalName: nameForm === 'ENTITY' ? values.legalName : null,
    },
    address: {
      street: values.street,
      postcode: values.postcode,
      city: values.city,
      country,
    },
    ...vatBlock(values, false),
    companyForm: values.companyForm || null,
    businessRegistration,
    iban: typeof values.iban === 'string' ? values.iban.replace(/\s+/g, '').toUpperCase() : null,
    permanentEstablishments: [],
    birthInfo: dobIso
      ? { date: dobIso, city: values.birthCity, country: values.birthCountry || country }
      : null,
  };
};

// TODO(DAC7 phase 6): Add hard payout/Pro-contract gating. Block payout
// activation and Pro-contract acceptance when tax.schemaVersion < 1.

export const saveTaxInformation = values => dispatch => {
  dispatch(saveTaxInformationRequest());
  let payload;
  try {
    payload = buildPayload(values);
  } catch (e) {
    dispatch(saveTaxInformationError(storableError(e)));
    return Promise.reject(e);
  }
  return saveTaxInformationApi(payload)
    .then(response => {
      dispatch(saveTaxInformationSuccess(response));
      // The record lives in currentUser.privateData — refresh it so the form
      // reinitializes from what was actually saved.
      dispatch(fetchCurrentUser());
      return response;
    })
    .catch(e => {
      console.error('saveTaxInformation failed:', e);
      // A 400 carries `{ errors: [...] }`; the form needs the codes (for
      // example vat.number.unverified). The body is unread at this point.
      const withBody =
        e.apiResponse && typeof e.apiResponse.json === 'function'
          ? e.apiResponse.json().catch(() => ({}))
          : Promise.resolve({});
      return withBody.then(body =>
        dispatch(saveTaxInformationError({ ...storableError(e), errors: body.errors || [] }))
      );
    });
};
